import React from 'react';
import { FiX, FiChevronDown } from 'react-icons/fi';
import './GroupPreview.css'; 

const GroupPreview = ({
  show,
  onClose,
  group,
  detailedUsers, 
  targetingRuleData, 
  usedInCampaigns,
  loading,
  error
}) => {
  if (!show) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (e) {
      console.error("Failed to parse date:", dateString, e);
      return 'Invalid Date';
    }
  };



  if (loading) {
    return (
      <div className="group-preview-modal-overlay">
        <div className="group-preview-modal-content" role="dialog" aria-modal="true" aria-labelledby="loading-preview-title">
          <div className="group-preview-modal-header">
            <h2 id="loading-preview-title" className="group-preview-modal-title">Loading Group Preview...</h2>
            <button className="group-preview-modal-close-btn" onClick={onClose} aria-label="Close preview">
              <FiX />
            </button>
          </div>
          <div className="group-preview-modal-body" style={{ textAlign: 'center', padding: '50px' }}>
            <p>Loading user group data...</p>
            <div className="group-preview-loading-spinner" style={{ margin: '20px auto' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="group-preview-modal-overlay">
        <div className="group-preview-modal-content" role="dialog" aria-modal="true" aria-labelledby="error-preview-title">
          <div className="group-preview-modal-header">
            <h2 id="error-preview-title" className="group-preview-modal-title">Error Loading Group</h2>
            <button className="group-preview-modal-close-btn" onClick={onClose} aria-label="Close preview">
              <FiX />
            </button>
          </div>
          <div className="group-preview-modal-body" style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="group-preview-modal-overlay">
        <div className="group-preview-modal-content" role="dialog" aria-modal="true" aria-labelledby="no-group-title">
          <div className="group-preview-modal-header">
            <h2 id="no-group-title" className="group-preview-modal-title">Group Not Found</h2>
            <button className="group-preview-modal-close-btn" onClick={onClose} aria-label="Close preview">
              <FiX />
            </button>
          </div>
          <div className="group-preview-modal-body" style={{ textAlign: 'center', padding: '50px' }}>
            <p>Detailed group data could not be retrieved.</p>
          </div>
        </div>
      </div>
    );
  }

  const safeDetailedUsers = detailedUsers || [];

  const displayUserCount = group.is_dynamic
    ? (group.targeting_rule?.users_count || 0) 
    : safeDetailedUsers.length; 


  return (
    <div className="group-preview-modal-overlay">
      <div className="group-preview-modal-content" role="dialog" aria-modal="true" aria-labelledby="group-preview-title">
        <div className="group-preview-modal-header">
          <h2 id="group-preview-title" className="group-preview-modal-title">Group Preview - {group.name || 'N/A'}</h2>
          <button className="group-preview-modal-close-btn" onClick={onClose} aria-label="Close preview">
            <FiX />
          </button>
        </div>

        <div className="group-preview-modal-body">
          <div className="group-preview-section group-preview-group-details-section">
            <h3>Group Details</h3>
            <div className="group-preview-group-details-content">
              <div className="group-preview-details-row">
                <div className="group-preview-detail-item">
                  <span className="group-preview-detail-label">Type</span>
                  <span className="group-preview-detail-value">{group.is_dynamic ? 'Dynamic' : 'Static' || 'N/A'}</span>
                </div>
                <div className="group-preview-detail-item group-preview-user-count-item">
                  <span className="group-preview-detail-label">User Count</span>
                  <span className="group-preview-detail-value">{displayUserCount.toLocaleString()}</span>
                </div>
              </div>
              <div className="group-preview-detail-item group-preview-description-item">
                <span className="group-preview-detail-label">Description</span>
                <span className="group-preview-detail-value">{group.description || 'No description provided.'}</span>
              </div>
            </div>
          </div>

          <div className="group-preview-section group-preview-targeting-rule-section">
            <h3>Targeting Rule</h3>
            {group.is_dynamic ? (
                group.targeting_rule ? ( 
                  <p className="group-preview-rule-name">
                      <strong className="group-preview-rule-summary-label">Name:</strong> {group.targeting_rule.name || 'N/A'}
                  </p>
                ) : (
                  <p>No specific targeting rule assigned to this dynamic group.</p>
                )
            ) : (
                <p>This is a <strong className="group-preview-rule-summary-label">Static</strong> group. Users are explicitly assigned and no dynamic targeting rule applies.</p>
            )}
          </div>

          <div className="group-preview-section group-preview-used-in-section">
            <h3>Used In</h3>
            {usedInCampaigns && usedInCampaigns.length > 0 ? (
                <ul className="group-preview-used-in-list">
                {usedInCampaigns.map((item, index) => (
                    <li key={index}>{item}</li>
                ))}
                </ul>
            ) : (
                <p>Not used in any campaigns.</p>
            )}
          </div>

           <h3 className="group-preview-users-header">
              Users <span className="group-preview-user-count-badge">{displayUserCount.toLocaleString()}</span>
            </h3>
          <div className="group-preview-section group-preview-users-table-section">
            <div className="group-preview-table-wrapper">
              <table className="group-preview-data-table">
                <thead>
                  <tr>
                    <th className="group-preview-table-header-cell"><div>ID <FiChevronDown /></div></th>
                    <th className="group-preview-table-header-cell"><div>Name <FiChevronDown /></div></th>
                    <th className="group-preview-table-header-cell"><div>Email <FiChevronDown /></div></th>
                    <th className="group-preview-table-header-cell"><div>Last Login <FiChevronDown /></div></th>
                  </tr>
                </thead>
                <tbody>
                  {safeDetailedUsers.length > 0 ? (
                    safeDetailedUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="group-preview-table-cell">{user.id}</td>
                        <td className="group-preview-table-cell">{user.name || 'N/A'}</td>
                        <td className="group-preview-table-cell">{user.email || 'N/A'}</td>
                        <td className="group-preview-table-cell">{formatDate(user.last_login_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="group-preview-table-cell" style={{textAlign: 'center', color: '#888'}}>
                        {group.is_dynamic
                         ? "Detailed user list is not displayed for dynamic groups due to potential size and dynamic nature. Count is based on targeting rules."
                         : "No detailed user data available for this static group."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupPreview;